import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {getCurrentPageDuration} from '@Components/poster-editor/poster-editor-reducer';
import {POSTER_EDITOR_WEB_BOTTOM_BAR_CONTAINER_ID} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.types';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import styles from './hover-overlay.module.scss';
import {useAppSelector} from '@/hooks';

export function HoverOverlay(): ReactElement | null {
  const setCurrentTime = (newValue: number | number[]): void => {
    void window.posterEditor?.whiteboard?.seek(newValue as number);
  };
  const isTimelineExpanded = useAppSelector((state) => {
    return state.posterEditor.isBottomWebSeekbarExpanded;
  });
  const SLIDER_HANDLE_RADIUS = 10;
  const [isBeingHovered, setIsBeingHovered] = useState<boolean>(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const currentPageDuration = useAppSelector(getCurrentPageDuration);

  const railMouseEnterHandler = (): void => {
    setIsBeingHovered(true);
  };
  const railMouseLeaveHandler = (): void => {
    setIsBeingHovered(false);
  };

  useEffect(() => {
    const sliderRail = document.querySelector(`#${POSTER_EDITOR_WEB_BOTTOM_BAR_CONTAINER_ID} .rc-slider`);

    sliderRail?.addEventListener('pointerenter', railMouseEnterHandler);
    sliderRail?.addEventListener('pointerleave', railMouseLeaveHandler);

    return () => {
      sliderRail?.removeEventListener('pointerenter', railMouseEnterHandler);
      sliderRail?.removeEventListener('pointerleave', railMouseLeaveHandler);
    };
  }, []);

  const getHoverOverlayWidth = (): string => {
    const sliderRail = document.querySelector(`#${POSTER_EDITOR_WEB_BOTTOM_BAR_CONTAINER_ID} .rc-slider`);
    let sliderX = 0;
    if (sliderRail) {
      sliderX = sliderRail?.getBoundingClientRect().x;
    }

    if (isBeingHovered) {
      return `${cursorPosition - sliderX + (sliderX < 0 ? 0 : SLIDER_HANDLE_RADIUS / 2)}px`;
    }
    return `0px`;
  };

  const handleMouseMove = (e: MouseEvent): void => {
    if (!isBeingHovered) {
      return;
    }
    setCursorPosition(e.clientX);
  };

  useEffect((): void => {
    window.addEventListener('mousemove', (e): void => {
      handleMouseMove(e);
    });
  });

  const getDummySliderAtTrackEnd = (): ReactElement | null => {
    if (isTimelineExpanded) {
      return null;
    }

    return (
      <ClickableDiv
        onMouseEnter={(): void => {
          setIsBeingHovered(true);
        }}
        onMouseLeave={(): void => {
          setIsBeingHovered(false);
        }}
        onClick={(): void => {
          if (currentPageDuration) {
            setCurrentTime(currentPageDuration);
          }
        }}
        className={styles.dummySliderTrackEnd}
      />
    );
  };

  return (
    <>
      {getDummySliderAtTrackEnd()}
      <div
        style={{
          width: getHoverOverlayWidth(),
        }}
        className={`${styles.hoverOverlay} hover-transition-all`}
      />
    </>
  );
}
